/* import __COLOCATED_TEMPLATE__ from './development.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */
/* eslint-disable no-console */

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { ref } from 'ember-ref-bucket';
import { inject as service } from '@ember/service';

import Component from '@glimmer/component';
import IntercomAdapter from 'embercom/lib/frame-adapter';
import { FrameOutwardEvents, FrameInwardEvents } from 'embercom/lib/frame-adapter';
import { getOwner } from '@ember/application';
import type ApplicationInstance from '@ember/application/instance';

//@ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

interface Args {
  externalId: string;
  externalType: string;
  appIdCode: string;
  theme: 'light' | 'dark';
  setParams: (paramName: string, paramValue: string) => void;
}

export default class StandaloneCopilotDevelopment extends Component<Args> {
  @ref('public-rich-text-editor') declare publicRichTextEditor: HTMLDivElement;
  @ref('public-textarea') declare publicTextarea: HTMLTextAreaElement;
  @ref('private-textarea') declare privateTextarea: HTMLTextAreaElement;
  @ref('email-textarea') declare emailTextarea: HTMLTextAreaElement;

  @service declare notificationsService: any;

  @tracked isExternalComposerVisible = true;

  originalExternalType = this.args.externalType;
  originalExternalId = this.args.externalId;
  originalTheme = this.args.theme;

  declare intercomAdapter: IntercomAdapter;

  get isSalesforceExternalType() {
    return (
      this.args.externalType === 'SALESFORCE_CASE' ||
      this.args.externalType === 'SALESFORCE_IN_APP_MESSAGE_CONVERSATION'
    );
  }

  @action setupFrame(iframe: HTMLIFrameElement) {
    this.intercomAdapter = new IntercomAdapter({
      owner: getOwner(this) as ApplicationInstance,
      listenWindow: window,
      sendWindow: iframe.contentWindow!,
      developmentFrameUrl: '*',
    });

    this.intercomAdapter.on(
      FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_PUBLIC_CONTENT,
      (payload: any) => {
        let { text, plainText } = payload;
        console.log('Received text:', text);
        console.log('Received plainText:', plainText);
        this.setContentToEmailComposer(sanitizeHtml(text));
        this.publicRichTextEditor.innerHTML = sanitizeHtml(text);
        this.publicTextarea.value = plainText;
        this.privateTextarea.value = plainText;
      },
    );

    this.intercomAdapter.on(
      FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_PRIVATE_CONTENT,
      (payload: any) => {
        let { text, plainText } = payload;
        console.log('Received text:', text);
        console.log('Received plainText:', plainText);
        this.setContentToEmailComposer(sanitizeHtml(text));
        this.publicRichTextEditor.innerHTML = sanitizeHtml(text);
        this.publicTextarea.value = plainText;
        this.privateTextarea.value = plainText;
      },
    );

    this.intercomAdapter.on(FrameOutwardEvents.IC_FRAME_ADD_COMPOSER_CONTENT, (payload: any) => {
      let { text, plainText } = payload;
      console.log('Received text:', text);
      console.log('Received plainText:', plainText);
      this.setContentToEmailComposer(sanitizeHtml(text));
      this.publicRichTextEditor.innerHTML = sanitizeHtml(text);
      this.publicTextarea.value = plainText;
      this.privateTextarea.value = plainText;
    });

    this.intercomAdapter.on(
      FrameOutwardEvents.IC_FRAME_GET_SALESFORCE_COMPOSER_VISIBILITY_STATE,
      () => {
        this.intercomAdapter.send(
          FrameInwardEvents.IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE,
          this.isExternalComposerVisible,
        );
      },
    );

    this.intercomAdapter.on(FrameOutwardEvents.IC_FRAME_GET_CURRENT_COMPOSER_CONTENT, () => {
      let text = '';
      let type = 'public';

      if (this.emailTextarea?.innerHTML || this.emailTextarea?.value) {
        text = document.getSelection()!.toString();
        type = 'salesforce_email_composer';
      } else if (this.publicRichTextEditor?.innerHTML || this.publicTextarea?.value) {
        text = this.publicRichTextEditor?.innerHTML || this.publicTextarea?.value;
      } else if (this.privateTextarea?.value) {
        text = this.privateTextarea?.value;
        type = 'private';
      }

      this.intercomAdapter.send(FrameInwardEvents.IC_FRAME_CURRENT_COMPOSER_CONTENT, {
        text,
        type,
      });
    });
  }

  private setContentToEmailComposer(text: string) {
    if (this.emailTextarea?.innerText?.trim() !== '') {
      let selection = document.getSelection()!;

      if (selection.rangeCount > 0) {
        let range = selection.getRangeAt(0);
        if (selection.type === 'Range') {
          range.deleteContents();
        }

        range.insertNode(range.createContextualFragment(sanitizeHtml(text)));

        // unselect the text that is just inserted
        selection.empty();
        return;
      }
    }
  }

  @action onConversationChange() {
    this.intercomAdapter.send(FrameInwardEvents.IC_FRAME_CONVERSATION_CHANGED, {
      external_id: this.args.externalId,
      external_type: this.args.externalType,
    });

    this.notificationsService.notifyConfirmation(
      `Conversation changed to ${this.args.externalId} ${this.args.externalType}`,
    );
  }

  @action setExternalId(id: string) {
    this.args.setParams('external_id', id);
  }

  @action setExternalType(type: string) {
    this.args.setParams('external_type', type);
  }

  @action setTheme(theme: 'light' | 'dark') {
    this.args.setParams('theme', theme);

    this.intercomAdapter.send(FrameInwardEvents.IC_FRAME_COLOR_SCHEME_CHANGED, {
      theme,
    });
  }

  @action toggleComposerVisibility() {
    this.isExternalComposerVisible = !this.isExternalComposerVisible;

    if (this.intercomAdapter) {
      console.log('Sending composer visibility state to frame:', this.isExternalComposerVisible);
      this.intercomAdapter.send(
        FrameInwardEvents.IC_FRAME_SALESFORCE_COMPOSER_VISIBILITY_STATE,
        this.isExternalComposerVisible,
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Copilot::Development': typeof StandaloneCopilotDevelopment;
  }
}
